import { COLOR_PRIMARY } from '../../routes/color-constants'
import { AppTable, Field } from '../../components/table'
import React, { FormEvent, useEffect, useMemo, useState } from 'react'
import { Actions, Pager, Sort } from '../../components/table/types'
import editIcon from '../../assets/table-icons/edit-icon.svg'
import deleteIcon from '../../assets/table-icons/delete-icon.svg'
import { useTranslation } from 'react-i18next'
import { Query, QueryParam, SortParam } from '../../common/api/Query'
import { ItemList } from '../../common/models/ItemList'
import { IUserService } from '../../modules/users/services/UserService'
import { getUserContainer } from '../../container/user-modules'
import { USER_SERVICE_KEY } from '../../modules/users'
import { User, UserQuery } from '../../modules/users/models/User'
import { navigate } from '@reach/router'
import { URL_USER } from '../../routes/routes-constants'
import { Box, Button, Grid, InputLabel, MenuItem, Modal, Select, Typography } from '@material-ui/core'
import { useStyles } from '../matchings/Matching.styles'
import { RoleType } from '../../modules/users/enums/RoleType'
import { getAuthContainer } from '../../container/auth-modules'
import { AuthService } from '../../modules/auth/services/AuthService'
import { AUTH_SERVICE_KEY } from '../../modules/auth'
import { Permission } from '../../common/enums/Permissions'
import { Kit, kitTitle } from 'modules/clinics/enums/KitsType'
import { getClinicContainer } from 'container/clinic-modules'
import { ClinicService } from 'modules/clinics/services/ClinicService'
import { CLINIC_SERVICE_KEY } from 'modules/clinics'
import { Clinic } from 'modules/clinics/models/Clinic'
import { getProjectContainer } from 'container/project-module'
import { ProjectService } from 'modules/projects/services/ProjectService'
import { PROJECT_SERVICE_KEY } from 'modules/projects'
import { Project, ProjectQuery } from 'modules/projects/models/Project'
import { FormAction, FormActions } from 'common/utils/form-generation'
import { commonStyles } from "common/styles/Styles";
const userService = getUserContainer().get<IUserService>(USER_SERVICE_KEY)
const authService = getAuthContainer().get<AuthService>(AUTH_SERVICE_KEY)
const clinicService = getClinicContainer().get<ClinicService>(CLINIC_SERVICE_KEY)
const projectService = getProjectContainer().get<ProjectService>(PROJECT_SERVICE_KEY)

const options: any[] = [
  Kit.PGD,
  Kit.RECEPTIVITY,
  Kit.PG_SEQ,
  Kit.INMUNOOCOMP,
  Kit.ALL
]
export const Table = () => {
  const { t } = useTranslation()
  const classes = useStyles({ color: COLOR_PRIMARY })
  const styles = commonStyles()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [page, setPage] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(10)
  const [items, setItems] = useState<User[]>([])
  const [acceptedData, setAcceptedData] = useState<User[]>([])
  const [pending, setPendings] = useState<User[]>([])
  const [pendingPage, setPendingPage] = useState<number>(0)
  const [pendingItemsPerPage, setPendingItemsPerPage] = useState<number>(10)
  const [pendingData, setPendingData] = useState<User[]>([])
  const [clinics, setClinics] = useState<Clinic[]>([])
  const [changeModal, setChangeModal] = useState<boolean>(false)
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<User>()
  const [newUserProject, setNewUserProject] = useState<User>()
  const [userProjects, setUserProjects] = useState<Project[]>([])
  const [selectOption, setSelectOption] = useState<Kit>(Kit.ALL)
  const [sort, setSort] = useState<SortParam<User>>({
    field: 'firstName',
    desc: true,
  })

  useEffect(() => {
    if (!isLoading) {
      return
    }

    const query: QueryParam<UserQuery>[] = []
    if (!authService.get().permissions.find((p) => p === Permission.editAllUsers)) {
      query.push(new QueryParam<UserQuery>('role', RoleType.ClinicUser))
      query.push(new QueryParam<UserQuery>('clinics', authService.get().clinics))
    }

    const fetchUsers = (limit: number) =>
      userService.getTable(
        new Query({
          pager: { offset: 0, limit },
          query,
          sort: [{ field: sort.field === 'clinicName' ? 'clinic_names' : sort.field, desc: !sort.desc }],
        }),
      )

    const processResponse = (res: ItemList<User>) => {
      const pendingUsers: User[] = []
      const acceptedUsers: User[] = []
      res.items.forEach((i) => {
        if (i.disabled || i.initPass) {
          pendingUsers.push(i)
        } else {
          acceptedUsers.push(i)
        }
      })
      setAcceptedData(acceptedUsers)
      setPendingData(pendingUsers)
      setPage(0)
      setPendingPage(0)
      setIsLoading(false)
    }

    fetchUsers(1000).subscribe((res) => {
      if (res.count > res.items.length) {
        fetchUsers(res.count).subscribe((fullRes) => processResponse(fullRes))
      } else {
        processResponse(res)
      }
    })
  }, [isLoading, sort])

  useEffect(() => {
    clinicService.getAllClinics().subscribe((res) => {
      if (res) {
        setClinics(res.items)
      }
    })
  }, [])

  const filteredAccepted = useMemo(() => {
    if (selectOption === Kit.ALL) {
      return acceptedData
    }

    return acceptedData.filter((user) => {
      if (user.clinicName === 'Admin') {
        return true
      }

      const clinic = clinics.find((c) => c.name === user.clinicName)

      if (!clinic) {
        return false
      }

      switch (selectOption) {
        case Kit.PGD:
          return clinic.activePgd
        case Kit.PG_SEQ:
          return clinic.activePgsec
        case Kit.INMUNOOCOMP:
          return clinic.activeInmunocom
        case Kit.RECEPTIVITY:
          return clinic.activeReceptiveness
        default:
          return true
      }
    })
  }, [acceptedData, clinics, selectOption])

  useEffect(() => {
    const offset = page * itemsPerPage
    setItems(filteredAccepted.slice(offset, offset + itemsPerPage))
  }, [filteredAccepted, page, itemsPerPage])

  useEffect(() => {
    const offset = pendingPage * pendingItemsPerPage
    setPendings(pendingData.slice(offset, offset + pendingItemsPerPage))
  }, [pendingData, pendingPage, pendingItemsPerPage])

  const handlePaginationChange = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(event.target.value, 10)
    const newRows = Number.isNaN(value) ? 10 : value
    setItemsPerPage(newRows)
    setPage(0)
  }

  const handlePendingPaginationChange = (event: unknown, value: number) => setPendingPage(value)

  const handleChangePendingRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(event.target.value, 10)
    const newRows = Number.isNaN(value) ? 10 : value
    setPendingItemsPerPage(newRows)
    setPendingPage(0)
  }

  const pager: Pager = {
    page,
    count: filteredAccepted.length,
    handleChangePage: handlePaginationChange,
    rowsPerPage: itemsPerPage,
    handleChangeRowsPerPage,
  }

  const pendingPager: Pager = {
    page: pendingPage,
    count: pendingData.length,
    handleChangePage: handlePendingPaginationChange,
    rowsPerPage: pendingItemsPerPage,
    handleChangeRowsPerPage: handleChangePendingRowsPerPage,
  }

  const fields: Field<User>[] = [
    {
      sortable: true,
      label: t('firstName'),
      name: 'firstName',
    },
    {
      sortable: true,
      label: t('lastName'),
      name: 'lastName',
    },
    {
      label: t('role'),
      name: 'role',
      renderFunc: (f, i) => t(RoleType[i.role]),
    },
    {
      sortable: true,
      label: t('email'),
      name: 'email',
    },
    {
      sortable: true,
      label: t('Clinics'),
      name: 'clinicName',
    },
    {
      sortable: true,
      label: t('createdAt'),
      name: 'createdAt',
      renderFunc: (f, i) => {
        const date = new Date(i.createdAt);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()); // Convertir a string
    
        // Suponiendo que tienes el formato en una variable
        const format = t('dateFormat'); // o "dd/MM/yyyy", dependiendo del idioma
    
        return format
            .replace('MM', month)
            .replace('dd', day)
            .replace('yyyy', year);
    } 
    },
  ]

    const fields2: Field<User>[] = [
    {
      sortable: true,
      label: t('firstName'),
      name: 'firstName',
    },
    {
      sortable: true,
      label: t('lastName'),
      name: 'lastName',
    },

    {
      sortable: true,
      label: t('email'),
      name: 'email',
    },

  ]

      const closeModal = () => {
          setDeleteModal(false)
          setChangeModal(false)
          setSelectedUser(undefined)
      }
  
      const actionsDelete: FormAction[] = [
          {
              label: t('close'),
              handleAction: closeModal
          }
      ]

  const editUser = (user: User) => navigate(URL_USER.replace(':id', `${user.id}`))

  const acept = (user: User) => userService.update(user)

  const hideUser = (u: User) => !authService.userCan(Permission.editUsers) || u.id === authService.get().id

  const deleteUser = (u: User) => {
    setSelectedUser(u)
    projectService.getByUserID(new Query({
              query: [
                new QueryParam<ProjectQuery>('userID', u.id),
              ],
            })).subscribe(res=>{
              if (res.count > 0) {
                setUserProjects(res.items)
                setChangeModal(true)
              } else{
                 setDeleteModal(true)
                }
            })
  }

  const handleChange = (e: FormEvent<HTMLFormElement>) =>{

    if(newUserProject){
      
    userProjects.forEach(p => {
      let aux = p.toDTO()
      aux.userID = newUserProject.id
      projectService.update(aux).subscribe()
    })

    handleDelete()
     }
   }

  const handleDelete = () => {
    if(selectedUser){
      userService.delete(selectedUser.id).subscribe()
    }
  }

  const actions: Actions<User> = {
    actionsColumn: t('Actions'),
    items: [
      {
        handler: editUser,
        icon: editIcon,
        label: t('Edit'),
        hidden: hideUser,
      },
      {
        handler: deleteUser,
        icon: deleteIcon,
        label: t('delete'),
        hidden: hideUser,
      }
    ],
  }

    const actions2: Actions<User> = {
    actionsColumn: t('Actions'),
    items: [
      {
        handler: editUser,
        icon: editIcon,
        label: t('Edit'),
        hidden: hideUser,
      },
      {
        handler: deleteUser,
        icon: deleteIcon,
        label: t('delete'),
        hidden: hideUser,
      }
    ],
  }
  const sortable: Sort<User> = {
    name: sort.field,
    direction: sort.desc ? 'desc' : 'asc',
    handleSort: (field) => {
      setSort({ field: field, desc: sort.field === field ? !sort.desc : true })
      setIsLoading(true)
    },
  }

  const handleFilter = (kit: Kit) => {
    setSelectOption(kit)
    setPage(0)
  }

  const createUsers = () => navigate(URL_USER.replace(':id', 'create'))

  return (
    <>
      <Box justifyContent={'space-between'} mb={2}>
      {authService.userCan(Permission.editUsers) && (
          <Button
            className={classes.button}
            variant={'contained'}
            style={{ float: 'right' }}
            onClick={createUsers}>
            {t('create')}
          </Button>
        )}
      <Grid item xs={9} className={classes.scriptSelectGrid}>
          <InputLabel>{t('kit')}</InputLabel>
          <Select
            fullWidth
            variant="outlined"
            value={selectOption}
            className={classes.scriptSelect}
            onChange={(e) =>handleFilter(e.target.value as Kit)}>
            {options.map((l: Kit) => (
              <MenuItem value={l} key={l}>
                {t(kitTitle()[l])}
              </MenuItem>
            ))}
          </Select>
          </Grid>
       
      </Box>
      <Box>
      
      <AppTable
        styleHeader={{ color: COLOR_PRIMARY }}
        actions={actions}
        fields={fields}
        items={items}
        rowKeyField={'id'}
        pager={pager}
        sort={sortable}
      />
      </Box>
     {authService.userCan(Permission.editAllUsers) && (
        <Box>
          <Typography
            style={{
              fontSize: '17px',
              fontWeight: 'bold',
              color: 'rgb(125, 191, 220)',
              marginBottom: '0.5%',
              marginTop: '50px'
            }}
            align="left">
            {t('pendingUsers')}
          </Typography>
          <AppTable
            styleHeader={{ color: COLOR_PRIMARY }}
            actions={actions2}
            fields={fields2}
            items={pending}
            rowKeyField={'id'}
            pager={pendingPager}
            sort={sortable}
          />
        </Box>
      )}
        <Modal open={deleteModal}>
        <Box className={styles.modal}>
          <form onSubmit={handleDelete}>
            <Box mt={2} textAlign={'justify'}>
              {t('deleteUserConfirmed')}
            </Box>
            <FormActions actions={actionsDelete} message={t('confirm')} />
          </form>
        </Box>
      </Modal>

      <Modal open={changeModal}>
        <Box className={styles.modal}>
          <form onSubmit={handleChange}>
            <Box mt={2} textAlign={'justify'}>
              <Select
                fullWidth
                variant="outlined"
                value={newUserProject?.id}
                className={classes.scriptSelect}
                onChange={(e) =>setNewUserProject(acceptedData[e.target.value as number])}
                >
                {acceptedData.map((l: User, i) => (
                  selectedUser?.clinicName === l.clinicName && 
                  l.id !== selectedUser.id && (
                  <MenuItem value={i} key={l.id}>
                    {l.firstName + " " + l.lastName}
                  </MenuItem>)
                ))}
              </Select>
            </Box>
            <FormActions actions={actionsDelete} message={t('confirm')} />
          </form>
        </Box>
      </Modal>
      

    </>
  )
}
